import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Paper,
  CircularProgress,
  FormHelperText,
} from '@mui/material'
import {
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
// import { PatientProfile } from '../../services/patientProfileService'
import {
  getPatientProfile,
  updatePatientProfile,
  uploadProfilePhoto,
} from '../../services/patientProfileService'
import { addNotification } from '../../store/slices/notificationSlice'
import api from '@/services/api'
import { AddressAutocomplete } from '../AddressAutocomplete'
import PhoneInput from '../ui/PhoneInput'
import { formatUSPhone, getUSPhoneErrorMessage } from '../../utils/phoneUtils'
import { capitalizeName } from '../../utils/validation'

// Mock options with default values
export const genderOptions = [
  'Male',
  'Female',
  // 'Non-binary',
  // 'Transgender',
  'Other',
  'Prefer not to say',
]

export const pronounOptions = ['He/Him', 'She/Her', 'They/Them', 'Other']

export const sessionFrequencyOptions = [
  'weekly',
  'bi-weekly',
  'monthly',
  'as-needed',
]

// Define proper interface for form data
interface PatientProfileFormData {
  firstName: string
  lastName: string
  email: string
  phone: string
  dateOfBirth: Date | null
  gender: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  country: string
  postalCode: string
  latitude?: number
  longitude?: number
}

const initialFormData: PatientProfileFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  dateOfBirth: null,
  gender: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
}

// Types for countries and states
interface Country {
  _id: string
  name: string
}

export const ProfileInfoSection: React.FC = () => {
  const dispatch = useDispatch()
  const [profile, setProfile] = useState<any>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] =
    useState<PatientProfileFormData>(initialFormData)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success'
  )
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)

  // Fetch profile data on component mount
  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      setLoading(true)
      const response = await getPatientProfile()
      const profileData = response

      if (!profileData) {
        throw new Error('No profile data received')
      }

      setProfile(profileData)

      const dob = profileData.dob ? new Date(profileData.dob) : null

      setFormData({
        firstName: profileData.firstName || '',
        lastName: profileData.lastName || '',
        email: profileData.email || '',
        phone: profileData.phone || '',
        dateOfBirth: dob,
        gender: profileData.gender || '',
        addressLine1: profileData.addressLine1 || '',
        addressLine2: profileData.addressLine2 || '',
        city: profileData.city || '',
        state: profileData.state || '',
        country: profileData.country || '',
        postalCode: profileData.postalCode || '',
        latitude: (profileData as any).latitude || undefined,
        longitude: (profileData as any).longitude || undefined,
      })
    } catch (error) {
      console.error('Error fetching profile:', error)
      showSnackbar('Failed to load profile data', 'error')
      dispatch(
        addNotification({
          type: 'error',
          title: 'Profile Error',
          message: 'Failed to load profile data. Please try again.',
        })
      )
    } finally {
      setLoading(false)
    }
  }

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' = 'success'
  ) => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }

  const handleInputChange = (
    field: keyof PatientProfileFormData,
    value: any
  ) => {
    // Automatically capitalize first letter for firstName and lastName
    let processedValue = value
    if (field === 'firstName' || field === 'lastName') {
      processedValue = capitalizeName(value as string)
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue,
    }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    } else {
      const phoneError = getUSPhoneErrorMessage(formData.phone)
      if (phoneError) {
        newErrors.phone = phoneError
      }
    }

    // Make address fields optional since they might not be in API response
    if (formData.addressLine1 && !formData.city.trim()) {
      newErrors.city = 'City is required if address is provided'
    }

    if (formData.addressLine1 && !formData.state.trim()) {
      newErrors.state = 'State is required if address is provided'
    }

    if (formData.addressLine1 && !formData.country.trim()) {
      newErrors.country = 'Country is required if address is provided'
    }

    if (formData.addressLine1 && !formData.postalCode.trim()) {
      newErrors.postalCode = 'Postal code is required if address is provided'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    try {
      setSaving(true)

      const profileData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        dob: formData.dateOfBirth
          ? formData.dateOfBirth.toISOString().split('T')[0]
          : undefined,
        gender: formData.gender || undefined,
        addressLine1: formData.addressLine1 || undefined,
        addressLine2: formData.addressLine2 || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        country: formData.country || undefined,
        postalCode: formData.postalCode || undefined,
        latitude: formData.latitude || undefined,
        longitude: formData.longitude || undefined,
      }

      const response = await updatePatientProfile(profileData)

      if (response && response.data) {
        setProfile(response.data)
      } else {
        await fetchProfile()
      }

      setIsEditing(false)

      showSnackbar('Profile updated successfully')
      dispatch(
        addNotification({
          type: 'success',
          title: 'Profile Updated',
          message: 'Your profile has been updated successfully.',
        })
      )
    } catch (error) {
      console.error('Error updating profile:', error)
      showSnackbar('Failed to update profile', 'error')
      dispatch(
        addNotification({
          type: 'error',
          title: 'Update Failed',
          message: 'Failed to update profile. Please try again.',
        })
      )
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (profile) {
      const dob = profile.dob ? new Date(profile.dob) : null

      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        phone: profile.phone || '',
        dateOfBirth: dob,
        gender: profile.gender || '',
        addressLine1: profile.addressLine1 || '',
        addressLine2: profile.addressLine2 || '',
        city: profile.city || '',
        state: profile.state || '',
        country: profile.country || '',
        postalCode: profile.postalCode || '',
        latitude: (profile as any).latitude || undefined,
        longitude: (profile as any).longitude || undefined,
      })
    }
    setIsEditing(false)
    setErrors({})
  }

  useEffect(() => {
    if (profile) {
      localStorage.setItem('profilePhotoUrl', profile?.profilePhotoUrl)
    }
  }, [profile])

  const handlePhotoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/avif']
    if (!validTypes.includes(file.type)) {
      showSnackbar(
        'Please select a valid image file (JPEG, JPG, PNG, AVIF)',
        'error'
      )
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('Image size should be less than 5MB', 'error')
      return
    }

    try {
      setUploadingPhoto(true)

      const { profilePhotoUrl } = await uploadProfilePhoto(file)

      if (!profilePhotoUrl) {
        showSnackbar('Failed to upload profile photo', 'error')
        return
      }

      setProfile(prev => (prev ? { ...prev, profilePhotoUrl } : prev))

      showSnackbar('Profile photo uploaded successfully', 'success')
      dispatch(
        addNotification({
          type: 'success',
          title: 'Photo Updated',
          message: 'Your profile photo has been updated successfully.',
        })
      )

      await fetchProfile()
    } catch (error: any) {
      console.error('Error uploading photo:', error)
      const errMsg =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to upload profile photo'
      showSnackbar(errMsg, 'error')
      dispatch(
        addNotification({
          type: 'error',
          title: 'Upload Failed',
          message: errMsg,
        })
      )
    } finally {
      setUploadingPhoto(false)
      if (event.target) event.target.value = ''
    }
  }

  const formatDate = (dateString: string) => {
    try {
      const datePart = dateString.split('T')[0]
      return new Date(datePart).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch (error) {
      return 'Invalid date'
    }
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!profile) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Failed to load profile data
        </Typography>
        <Button variant="contained" onClick={fetchProfile} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Box>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Profile Information
          </Typography>
          {!isEditing && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
            >
              Edit Profile
            </Button>
          )}
        </Box>

        <Grid container spacing={3}>
          {/* Profile Photo Section */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Profile Photo
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Avatar
                    src={profile.profilePhotoUrl}
                    sx={{
                      width: 120,
                      height: 120,
                      border: '4px solid',
                      borderColor: 'primary.main',
                    }}
                  >
                    {profile.firstName?.[0] || ''}
                    {profile.lastName?.[0] || ''}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Upload a new profile photo.
                    </Typography>
                    <input
                      accept="image/jpeg,image/jpg,image/png,image/avif"
                      style={{ display: 'none' }}
                      id="photo-upload"
                      type="file"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhoto}
                    />
                    <label htmlFor="photo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={
                          uploadingPhoto ? (
                            <CircularProgress size={16} />
                          ) : (
                            <PhotoCameraIcon />
                          )
                        }
                        disabled={uploadingPhoto}
                      >
                        {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                      </Button>
                    </label>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Personal Information */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Personal Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={isEditing ? formData.firstName : profile.firstName}
                      onChange={e =>
                        handleInputChange('firstName', e.target.value)
                      }
                      disabled={!isEditing}
                      required
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={isEditing ? formData.lastName : profile.lastName}
                      onChange={e =>
                        handleInputChange('lastName', e.target.value)
                      }
                      disabled={!isEditing}
                      required
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {isEditing ? (
                      <DatePicker
                        maxDate={new Date()}
                        label="Date of Birth"
                        value={formData.dateOfBirth}
                        onChange={date =>
                          handleInputChange('dateOfBirth', date)
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.dateOfBirth,
                            helperText: errors.dateOfBirth,
                          },
                        }}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        label="Date of Birth"
                        value={
                          profile.dob ? formatDate(profile.dob) : 'Not provided'
                        }
                        disabled
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={!isEditing}>
                      <InputLabel>Gender</InputLabel>
                      <Select
                        value={
                          isEditing ? formData.gender : profile.gender || ''
                        }
                        label="Gender"
                        onChange={e =>
                          handleInputChange('gender', e.target.value)
                        }
                      >
                        <MenuItem value="">
                          <em>Not specified</em>
                        </MenuItem>
                        {genderOptions.map(option => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Contact Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={isEditing ? formData.email : profile.email}
                      onChange={e => handleInputChange('email', e.target.value)}
                      disabled={!isEditing}
                      required
                      error={!!errors.email}
                      helperText={errors.email}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PhoneInput
                      fullWidth
                      label="Phone"
                      value={isEditing ? formData.phone : profile.phone}
                      onChange={value => handleInputChange('phone', value)}
                      disabled={!isEditing}
                      required
                      error={!!errors.phone}
                      helperText={errors.phone}
                      onValidationChange={isValid => {
                        if (!isValid && formData.phone) {
                          setErrors(prev => ({
                            ...prev,
                            phone: getUSPhoneErrorMessage(formData.phone),
                          }))
                        } else if (isValid) {
                          setErrors(prev => ({
                            ...prev,
                            phone: '',
                          }))
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Address Information */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Address Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {isEditing ? (
                      <>
                        <Typography
                          variant="body2"
                          sx={{ mb: 0.5, fontWeight: 500, color: 'grey.700' }}
                        >
                          Address Line 1
                        </Typography>
                        <AddressAutocomplete
                          sx={{
                            margin: 'normal',
                            fullWidth: true,
                            '& .MuiInputBase-root': {
                              height: 56,
                            },
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '1 !important' as any,
                            },
                            '& .MuiInputBase-input': {
                              py: '1.5rem',
                              fontSize: '1rem',
                            },
                          }}
                          value={formData.addressLine1}
                          onChange={(address, components) => {
                            handleInputChange('addressLine1', address)
                            // Only update fields if components are provided and have values
                            // Don't clear them when address is cleared (components will have empty strings)
                            if (components) {
                              if (components.city) {
                                handleInputChange('city', components.city)
                              }
                              if (components.state) {
                                handleInputChange('state', components.state)
                              }
                              if (components.zipCode || components.postalCode) {
                                handleInputChange(
                                  'postalCode',
                                  components.zipCode || components.postalCode
                                )
                              }
                              if (components.country) {
                                handleInputChange('country', components.country)
                              }
                              if (components.addressLine2) {
                                handleInputChange(
                                  'addressLine2',
                                  components.addressLine2
                                )
                              }
                              if (components.latitude !== undefined) {
                                handleInputChange(
                                  'latitude',
                                  components.latitude
                                )
                              }
                              if (components.longitude !== undefined) {
                                handleInputChange(
                                  'longitude',
                                  components.longitude
                                )
                              }
                            }
                          }}
                          placeholder="Start typing your address..."
                          error={!!errors.addressLine1}
                          helperText={errors.addressLine1}
                          addressType="address"
                        />
                      </>
                    ) : (
                      <TextField
                        fullWidth
                        label="Address Line 1"
                        value={profile.addressLine1 || ''}
                        disabled
                      />
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line 2"
                      value={
                        isEditing
                          ? formData.addressLine2
                          : profile.addressLine2 || ''
                      }
                      onChange={e =>
                        handleInputChange('addressLine2', e.target.value)
                      }
                      disabled={!isEditing}
                      helperText="Apartment, suite, unit, building, floor, etc."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={isEditing ? formData.city : profile.city || ''}
                      onChange={e => handleInputChange('city', e.target.value)}
                      disabled={!isEditing}
                      error={!!errors.city}
                      helperText={errors.city}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="State"
                      value={isEditing ? formData.state : profile.state || ''}
                      onChange={e => handleInputChange('state', e.target.value)}
                      disabled={!isEditing}
                      error={!!errors.state}
                      helperText={errors.state}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Postal Code"
                      value={
                        isEditing
                          ? formData.postalCode
                          : profile.postalCode || ''
                      }
                      onChange={e =>
                        handleInputChange('postalCode', e.target.value)
                      }
                      disabled={!isEditing}
                      error={!!errors.postalCode}
                      helperText={errors.postalCode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={isEditing ? formData.country : profile.country || ''}
                      onChange={e => handleInputChange('country', e.target.value)}
                      disabled={!isEditing}
                      error={!!errors.country}
                      helperText={errors.country}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Action Buttons */}
          {isEditing && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Box
                  sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={
                      saving ? <CircularProgress size={16} /> : <SaveIcon />
                    }
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  )
}
